import { memo } from 'react';

import { connect } from 'react-redux';
import { branch, compose, lifecycle, renderComponent, withHandlers } from 'recompact';

import { Loading } from 'src/app/layouts/Loading';
import type { Dispatch, RootState } from 'src/app/state/store';

/**
 * This sets a pre-condition on a page rendering. onFail will be triggered if the pre-condition fails.
 * The intention is for onFail to dispatch a redirect.
 *
 * @param {function(state): boolean} preconditionCheck A check if all preconditions are met
 * @param {function(dispatch, getState)} onFail
 *    A thunk that acts as consequences to a failure, will act when preconditions returns false.
 * @return {function(*=): *} An HoC
 */
export const withPreconditions = (preconditionCheck, onFail) => WrappedComponent => {
  const mapStateToProps = (state: RootState) => ({
    isFail: !preconditionCheck(state),
  });

  const mapDispatchToProps = (dispatch: Dispatch) => ({
    dispatchFail: () => dispatch(onFail),
  });

  return compose(
    connect(mapStateToProps, mapDispatchToProps),
    withHandlers({
      check:
        ({ isFail, dispatchFail }) =>
        async () => {
          if (isFail) {
            dispatchFail();
          }
        },
    }),
    lifecycle({
      componentDidMount() {
        const { check } = this.props;
        window.addEventListener('focus', check);

        check();
      },
      componentWillUnmount() {
        const { check } = this.props;
        window.removeEventListener('focus', check);
      },
    }),
    branch(({ isFail }) => isFail, renderComponent(Loading)),
    memo
  )(WrappedComponent);
};
